import React, { useState } from 'react';
import {
  Box,
  Typography,
  SvgIcon,
  Link,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Map, ShoppingBasket } from '@mui/icons-material';
import { getApiUrl } from '../config';
import {
  GlassBackground,
  GlassContainer,
  GlassButton,
  GlassInput,
  GlassShimmer,
  useGlassShimmer,
  GlassInfoCard,
} from './ui/GlassComponents';

const DiscordIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </SvgIcon>
);

const WikiIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    <path d="M8,12H16V14H8V12M8,16H13V18H8V16Z" />
  </SvgIcon>
);

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [uuid, setUuid] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useDirectUuid, setUseDirectUuid] = useState(false);
  const [activeTab, setActiveTab] = useState('signin'); // 'signin' or 'signup'

  const shimmerRef = useGlassShimmer('#login-card, .shimmer-button');

  // Function to resolve UUID from username using CORS proxy
  const resolveUuid = async (mcUsername) => {
    if (!mcUsername.trim()) return null;
    
    try {
      // Use a CORS proxy to access Mojang API
      const response = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(`https://api.mojang.com/users/profiles/minecraft/${mcUsername.trim()}`)}`);
      
      if (response.ok) {
        const proxyData = await response.json();
        const data = JSON.parse(proxyData.contents);
        
        if (data && data.id) {
          // Mojang API returns UUID without dashes, so we need to add them
          const rawUuid = data.id;
          const formattedUuid = [
            rawUuid.slice(0, 8),
            rawUuid.slice(8, 12),
            rawUuid.slice(12, 16),
            rawUuid.slice(16, 20),
            rawUuid.slice(20, 32)
          ].join('-');
          return formattedUuid;
        }
      }
      
      // Try alternative CORS proxy if first one fails
      const response2 = await fetch(`https://corsproxy.io/?${encodeURIComponent(`https://api.mojang.com/users/profiles/minecraft/${mcUsername.trim()}`)}`);
      
      if (response2.ok) {
        const data = await response2.json();
        if (data && data.id) {
          const rawUuid = data.id;
          const formattedUuid = [
            rawUuid.slice(0, 8),
            rawUuid.slice(8, 12),
            rawUuid.slice(12, 16),
            rawUuid.slice(16, 20),
            rawUuid.slice(20, 32)
          ].join('-');
          return formattedUuid;
        }
      }
      
      throw new Error('Minecraft username not found');
    } catch (err) {
      throw new Error('Failed to resolve UUID from username. Please check the username or use UUID directly.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let finalUuid = uuid;

    // If using direct UUID input, validate it
    if (useDirectUuid) {
      if (!uuid) {
        setError('Please enter a UUID');
        return;
      }
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(uuid)) {
        setError('Please enter a valid UUID format');
        return;
      }
    } else {
      // Using username, resolve UUID automatically
      if (!username) {
        setError('Please enter a username');
        return;
      }
      
      // Validate username format
      const usernameRegex = /^[a-zA-Z0-9_]{3,16}$/;
      if (!usernameRegex.test(username)) {
        setError('Please enter a valid Minecraft username (3-16 characters, letters, numbers, and underscores only)');
        return;
      }
    }

    if (!password) {
      setError('Please enter a password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Resolve UUID if using username
      if (!useDirectUuid) {
        try {
          finalUuid = await resolveUuid(username);
          if (!finalUuid) {
            setError('Could not resolve UUID for this username. Please check the username or use UUID directly.');
            setLoading(false);
            return;
          }
        } catch (err) {
          setError(err.message);
          setLoading(false);
          return;
        }
      }

      // Call the actual Statecraft API
      const response = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uuid: finalUuid,
          password: password
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Success - pass the result to parent component (NO password stored!)
        onLogin({
          ...result,
          uuid: finalUuid // Keep UUID for display purposes
        });
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to connect to server. Please make sure the Statecraft server is running.');
    } finally {
      setLoading(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <GlassBackground>
      {/* Shimmer shadow element */}
      <GlassShimmer ref={shimmerRef} />

      {/* Main content container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 1.5 : 6,
          width: '100%',
          maxWidth: isMobile ? '100%' : '1200px',
          mx: 'auto',
        }}
      >
        {/* Left side - Landfall logo and Navigation buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isMobile ? 1 : 4,
            width: isMobile ? '100%' : 'auto',
          }}
        >
          {/* Large Landfall logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: isMobile ? 0.5 : 2,
            }}
          >
            <img
              src="/landfall.png"
              alt="Landfall SMP"
              style={{
                height: isMobile ? '80px' : '120px',
                width: 'auto',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              }}
            />
          </Box>

          {/* Navigation buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              gap: isMobile ? 1 : 3,
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            {/* Discord Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://discord.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <DiscordIcon sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Discord'}
              </Button>
            </Box>

            {/* Store Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://store.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ShoppingBasket sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Store'}
              </Button>
            </Box>

            {/* Map Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://map.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Map sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Map'}
              </Button>
            </Box>

            {/* Wiki Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://wiki.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <WikiIcon sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Wiki'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Right side - Login form */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Login card */}
          <GlassContainer id="login-card" variant="loginCard">
            {/* Statecraft logo inside the card */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: isMobile ? 1.5 : 4,
              }}
            >
              <img
                src="/statecraft.png"
                alt="Statecraft"
                style={{
                  maxWidth: isMobile ? '200px' : '280px',
                  height: 'auto',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                }}
              />
            </Box>

            {/* Tab selector with flat glass layers */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                maxWidth: isMobile ? '320px' : '360px',
                height: isMobile ? '48px' : '56px',
                mb: isMobile ? 2 : 4,
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
              }}
            >
              {/* Sliding background indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '4px',
                  left: activeTab === 'signin' ? '4px' : 'calc(50% + 2px)',
                  height: 'calc(100% - 8px)',
                  width: 'calc(50% - 6px)',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 1,
                }}
              />
              
              <Box
                onClick={() => setActiveTab('signin')}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50%',
                  height: '100%',
                  cursor: 'pointer',
                  zIndex: 2,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'color 0.3s ease',
                  color: activeTab === 'signin' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                }}
              >
                Sign in
              </Box>
              <Box
                onClick={() => setActiveTab('signup')}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50%',
                  height: '100%',
                  cursor: 'pointer',
                  zIndex: 2,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'color 0.3s ease',
                  color: activeTab === 'signup' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                }}
              >
                Sign up
              </Box>
            </Box>

            {/* Content area with fixed width */}
            <Box
              sx={{
                width: '100%',
                minHeight: isMobile ? '280px' : '400px',
              }}
            >
              {activeTab === 'signin' ? (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontSize: '1.4rem',
                      fontWeight: 500,
                    }}
                  >
                    Sign in to your account
                  </Typography>

                  {error && (
                    <GlassInfoCard severity="error">
                      {error}
                    </GlassInfoCard>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    {/* Username/UUID Toggle */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setUseDirectUuid(!useDirectUuid);
                          setUsername('');
                          setUuid('');
                          setError('');
                        }}
                        sx={{
                          textTransform: 'none',
                          color: 'rgba(161, 161, 170, 1)',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'white',
                          },
                        }}
                      >
                        {useDirectUuid ? 'Use Minecraft Username Instead' : 'Use UUID Directly'}
                      </Button>
                    </Box>

                    {/* Username/UUID Input */}
                    <GlassInput
                      type="text"
                      value={useDirectUuid ? uuid : username}
                      onChange={(e) => useDirectUuid ? setUuid(e.target.value) : setUsername(e.target.value)}
                      disabled={loading}
                      placeholder={useDirectUuid ? "12345678-1234-1234-1234-123456789012" : "Steve"}
                    />

                    {/* Password Input */}
                    <Box sx={{ mb: 4 }}>
                      <GlassInput
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        placeholder="Enter your password"
                      />
                    </Box>

                    {/* Sign In Button - Using GlassButton component */}
                    <GlassButton
                      type="submit"
                      variant="glass"
                      size="medium"
                      disabled={loading}
                      sx={{ width: '100%' }}
                    >
                      {loading ? (useDirectUuid ? 'Authenticating...' : 'Resolving UUID & Authenticating...') : 'Sign in'}
                    </GlassButton>

                    {/* API Documentation Link */}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        <Link
                          href="https://api.statecraft.landfall.world/swagger-ui/"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'rgba(161, 161, 170, 1)',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'white',
                            },
                          }}
                        >
                          Click here to view the API documentation
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontSize: '1.4rem',
                      fontWeight: 500,
                    }}
                  >
                    Create an account
                  </Typography>

                  <Box
                    sx={{
                      textAlign: 'center',
                      padding: '32px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      background: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        color: 'rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      To sign up, load into our game server and type:
                    </Typography>
                    <Box
                      sx={{
                        mt: 2,
                        padding: '12px 16px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: '#00ff00',
                        textAlign: 'left',
                        wordBreak: 'break-all',
                      }}
                    >
                      /persona password "your_password_here"
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </GlassContainer>
        </Box>
      </Box>
    </GlassBackground>
  );
};

export default Login;